import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import {
  ActionButtons,
  IconButton,
  SummaryGrid,
} from "./personSummaryRow.styled";
import { LabeledValue } from "components/designV2/labeledValue/labeledValue";

export interface PersonSummaryLabels {
  shifts: string;
  plannedTime: string;
  approvedTime: string;
  toApprove: string;
  unreported: string;
  absence: string;
  deviation: string;
  download: string;
  collapse: string;
  expand: string;
}

export interface PersonSummaryMetrics {
  shifts: number;
  plannedTime: string;
  approvedTime: string;
  toApproveTime: string;
  unreportedTime: string;
  absenceTime: string;
  deviationTime: string;
}

interface PersonSummaryDetailsProps {
  name: string;
  role: string | null;
  teamLabel: string;
  avatarUrl: string | null;
  summary: PersonSummaryMetrics;
  isExpanded: boolean;
  isZeroLikeValue: (value: string) => boolean;
  labels: PersonSummaryLabels;
  onDownloadClick: () => void;
  onToggleExpand: () => void;
}

export const PersonSummaryDetails = (
  props: PersonSummaryDetailsProps,
): JSX.Element => {
  const tags = [props.teamLabel];
  const metrics: Array<{
    id: string;
    label: string;
    value: string;
    muted?: boolean;
  }> = [
    {
      id: "shifts",
      label: props.labels.shifts,
      value: String(props.summary.shifts),
      muted: false,
    },
    {
      id: "planned-time",
      label: props.labels.plannedTime,
      value: props.summary.plannedTime,
      muted: props.isZeroLikeValue(props.summary.plannedTime),
    },
    {
      id: "approved-time",
      label: props.labels.approvedTime,
      value: props.summary.approvedTime,
      muted: props.isZeroLikeValue(props.summary.approvedTime),
    },
    {
      id: "to-approve-time",
      label: props.labels.toApprove,
      value: props.summary.toApproveTime,
      muted: props.isZeroLikeValue(props.summary.toApproveTime),
    },
    {
      id: "unreported-time",
      label: props.labels.unreported,
      value: props.summary.unreportedTime,
      muted: props.isZeroLikeValue(props.summary.unreportedTime),
    },
    {
      id: "absence-time",
      label: props.labels.absence,
      value: props.summary.absenceTime,
      muted: props.isZeroLikeValue(props.summary.absenceTime),
    },
    {
      id: "deviation-time",
      label: props.labels.deviation,
      value: props.summary.deviationTime,
      muted: props.isZeroLikeValue(props.summary.deviationTime),
    },
  ];

  return (
    <SummaryGrid>
      <AvatarChip
        title={props.name}
        subTitle={props.role ?? ""}
        tags={tags}
        avatarUrl={props.avatarUrl}
      />
      {metrics.map((metric) => (
        <LabeledValue
          key={metric.id}
          value={metric.value}
          label={metric.label}
          muted={metric.muted}
        />
      ))}
      <ActionButtons>
        <IconButton
          type="button"
          aria-label={props.labels.download}
          onClick={props.onDownloadClick}
        >
          <CustomIcon name="v2/download" size="20px" color={ColorV2.Blue1} />
        </IconButton>
        <IconButton
          type="button"
          aria-label={
            props.isExpanded ? props.labels.collapse : props.labels.expand
          }
          aria-expanded={props.isExpanded}
          onClick={props.onToggleExpand}
        >
          <CustomIcon
            name={props.isExpanded ? "v2/up-chevron" : "v2/down-chevron"}
            size="20px"
            color={ColorV2.Blue1}
          />
        </IconButton>
      </ActionButtons>
    </SummaryGrid>
  );
};
